import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { tabsClasses } from '@mui/material/Tabs';
import { Box, LinearProgress, Tab, Tabs } from '@mui/material';

import useNotification from '../../../../core/hooks/use_notification';
import { getLanguagesState } from '../../../../features/product/get_languages';
import { getAutomaticCouponSettingNotificationData, getAutomaticCouponSettingTranslationNotificationData, getPodsNotificationData } from '../../../../core/utils/notification_utils';
import { getAutomaticCouponSettingAction, getAutomaticCouponSettingState, invalidateAutomaticCouponSetting } from '../../../../features/coupon/get_automatic_coupon_setting';
import { ROUTE_COUPONS } from '../../../../core/constants';
import AutomaticCouponSettingForm from './forms/automatic_coupon_setting_form';
import AutomaticCouponSettingTranslationForm from './forms/automatic_coupon_setting_translation_form';
import { getAllPodsAction, getAllPodsState } from '../../../../features/pod/get_all_pods';
import { getAutomaticCouponSettingTranslationAction, getAutomaticCouponSettingTranslationState } from '../../../../features/coupon/get_automatic_coupon_setting_translation';
import PageHeader from '../../../common/page_header';

const AutomaticCouponSettingFormsPage = () => {
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { automaticCouponType } = useParams();

    const getAutomaticCouponSetting = useSelector(getAutomaticCouponSettingState);
    const getAutomaticCouponSettingTranslation = useSelector(getAutomaticCouponSettingTranslationState);
    const getLanguages = useSelector(getLanguagesState);
    const getAllPods = useSelector(getAllPodsState);

    const [languageCode, setLanguageCode] = useState('PL');
    const [tab, setTab] = useState(0);

    useEffect(() => {
        dispatch(getAutomaticCouponSettingAction(automaticCouponType))
            .then(response => showErrorMessage(getAutomaticCouponSettingNotificationData(response)));
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));

        return () => {
            dispatch(invalidateAutomaticCouponSetting());
        };
    }, [dispatch, automaticCouponType, showErrorMessage]);

    useEffect(() => {
        dispatch(getAutomaticCouponSettingTranslationAction({ automaticCouponType, languageCode }))
            .then(response => showErrorMessage(getAutomaticCouponSettingTranslationNotificationData(response)));
    }, [dispatch, automaticCouponType, languageCode, showErrorMessage]);

    const renderView = () => {
        if (getAutomaticCouponSetting?.loading || getAllPods?.loading || getAutomaticCouponSettingTranslation?.loading) {
            return (
                <Box mt={ 6 } width="100%">
                    <LinearProgress/>
                </Box>
            );
        }

        if (getAutomaticCouponSetting?.data && getAllPods?.data) {
            if (tab === 0) {
                return (
                    <AutomaticCouponSettingForm
                        automaticCouponType={ automaticCouponType }/>
                );
            } else if (tab !== 0 && getAutomaticCouponSettingTranslation?.data) {
                return (
                    <AutomaticCouponSettingTranslationForm
                        automaticCouponType={ automaticCouponType }
                        languageCode={ languageCode }/>
                );
            }
        }

        return <></>;
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
            <PageHeader
                title="Ustawienia automatycznego kuponu"
                showBack={ true }
                onBack={ () => navigate(ROUTE_COUPONS) }
            />

            <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                <Tabs
                    value={ tab }
                    onChange={ (_, value) => {
                        setTab(value);
                        setLanguageCode(getLanguages?.data[value - 1]?.languageCode || 'PL');
                    } }
                    sx={ {
                        [`& .${ tabsClasses.scrollButtons }`]: {
                            '&.Mui-disabled': { opacity: 0.3 },
                        },
                    } }
                >
                    <Tab label="Informacje ogólne"/>
                    {
                        getLanguages?.data?.map(language => {
                            return <Tab key={ language?.languageCode } label={ language?.languageName }
                                        disabled={ !getAutomaticCouponSetting?.data?.discount }/>;
                        })
                    }
                </Tabs>
            </Box>

            { renderView() }
        </Box>
    );
};

export default AutomaticCouponSettingFormsPage;